// 管理者: セッション監視(設計書 §3.5.5)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiRequestRaw } from '../client';

export type AdminSessionState = 'starting' | 'running' | 'stopping' | 'completed' | 'failed';

export interface ActiveSession {
  ulid: string;
  performer: { ulid: string; display_name: string };
  server_slot: { server_name: string; slot: 'A' | 'B'; pid: number | null };
  streaming_profile: { name: string; platform: string };
  state: AdminSessionState;
  started_at: string;
  elapsed_seconds: number;
}

export interface AdminSessionRow {
  ulid: string;
  performer: { ulid: string; display_name: string };
  server_name: string;
  slot: 'A' | 'B';
  streaming_profile: { name: string; platform: string };
  state: AdminSessionState;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  termination_reason: string | null;
}

export interface AdminSessionsResponse {
  data: AdminSessionRow[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

export interface AdminSessionsQuery {
  page?: number;
  per_page?: number;
  performer_ulid?: string;
  server_ulid?: string;
  streaming_profile_ulid?: string;
  state?: AdminSessionState;
  from?: string;
  to?: string;
}

export const adminSessionKeys = {
  all: ['admin', 'sessions'] as const,
  active: ['admin', 'sessions', 'active'] as const,
  list: (q: AdminSessionsQuery) => ['admin', 'sessions', 'list', q] as const,
};

export function useActiveSessions() {
  return useQuery({
    queryKey: adminSessionKeys.active,
    queryFn: () => apiRequest<ActiveSession[]>('/admin/sessions/active'),
    refetchInterval: 5_000,
  });
}

export function useAdminSessions(params: AdminSessionsQuery) {
  return useQuery({
    queryKey: adminSessionKeys.list(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.page) qs.set('page', String(params.page));
      if (params.per_page) qs.set('per_page', String(params.per_page));
      if (params.performer_ulid) qs.set('performer_ulid', params.performer_ulid);
      if (params.server_ulid) qs.set('server_ulid', params.server_ulid);
      if (params.streaming_profile_ulid)
        qs.set('streaming_profile_ulid', params.streaming_profile_ulid);
      if (params.state) qs.set('state', params.state);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      const path = qs.toString() ? `/admin/sessions?${qs.toString()}` : '/admin/sessions';
      return await apiRequestRaw<AdminSessionsResponse>(path);
    },
    placeholderData: (prev) => prev,
  });
}

export function useForceStopSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ulid, reason }: { ulid: string; reason: string }) =>
      apiRequest<{ session_ulid: string; state: string }>(
        `/admin/sessions/${ulid}/force-stop`,
        { method: 'POST', body: { reason } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminSessionKeys.all });
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });
}

export const ADMIN_SESSION_STATE_LABELS: Record<AdminSessionState, string> = {
  starting: '開始中',
  running: '配信中',
  stopping: '停止処理中',
  completed: '完了',
  failed: '失敗',
};
